import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { supabase } from "../supabaseClient";
import { handleRedirect } from "./handleRedirect";
import { useAuth } from "../app/AuthContext";
import LoginSocial from "./LoginSocial";
import NotificationModal from "../components/NotificationModal";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({
    show: false,
    message: "",
    type: "",
    onAfterClose: null,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [verifyingSession, setVerifyingSession] = useState(true);
  const navigate = useNavigate();
  const { setUserType } = useAuth();

  useEffect(() => {
    const checkSession = async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (session) {
          await handleRedirect(session.user, navigate, setUserType);
        }
      } catch (err) {
        console.error("Erro ao verificar sessão inicial:", err);
      } finally {
        setVerifyingSession(false);
      }
    };

    checkSession();
  }, [navigate]);

  const closeNotification = () => {
    const callback = notification.onAfterClose;
    setNotification({ show: false, message: "", type: "", onAfterClose: null });
    if (callback) callback();
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setNotification({ show: false, message: "", type: "" });

    try {
      const { data, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (authError) {
        console.error("Erro detalhado do Supabase Auth:", authError);

        if (authError.message?.includes("Email not confirmed")) {
          setNotification({
            show: true,
            message:
              "Seu e-mail ainda não foi confirmado. Você pode continuar o cadastro em modo rascunho; confirme seu e-mail para habilitar compras e visualização de famílias/babás.",
            type: "info",
            onAfterClose: () => navigate("/perfil-novo"),
          });
          return;
        }

        if (
          authError.status === 0 ||
          authError.message?.toLowerCase().includes("fetch") ||
          authError.message?.includes("failed")
        ) {
          setNotification({
            show: true,
            message:
              "Não foi possível encontrar o servidor do Supabase (Erro de DNS). Verifique sua internet, tente trocar seu DNS para 8.8.8.8 ou verifique se o projeto não está pausado no painel do Supabase.",
            type: "error",
          });
          return;
        }

        setNotification({
          show: true,
          message: "Erro: E-mail ou senha inválidos.",
          type: "error",
        });
        return;
      }

      if (data?.user) {
        await handleRedirect(data.user, navigate, setUserType);
      } else {
        navigate("/");
      }
    } catch (err) {
      console.error("Erro crítico no login:", err);
      setNotification({
        show: true,
        message:
          "Não foi possível conectar ao servidor. Verifique sua conexão com a internet.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  if (verifyingSession) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <p className="text-sm text-slate-500">Verificando credenciais...</p>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-[linear-gradient(180deg,#fff_0%,#f8f5f1_100%)] px-4 py-6 sm:px-6 lg:px-8">
        <div className="mx-auto grid min-h-[calc(100vh-3rem)] w-full max-w-6xl overflow-hidden rounded-[32px] border border-warm-200 bg-white shadow-[0_24px_80px_rgba(46,39,32,0.10)] lg:grid-cols-[1.05fr_0.95fr]">
          <div className="relative flex flex-col justify-center overflow-hidden border-b border-warm-200 bg-[linear-gradient(180deg,#fff_0%,#fff7f3_100%)] px-6 py-10 text-warm-900 sm:px-10 lg:border-b-0 lg:border-r lg:px-14 lg:py-14">
            <div className="absolute inset-0 opacity-90">
              <div className="absolute -left-10 top-10 h-36 w-36 rounded-full bg-rose-100 blur-3xl" />
              <div className="absolute right-0 top-0 h-40 w-40 rounded-full bg-warm-100 blur-3xl" />
              <div className="absolute bottom-10 left-1/3 h-28 w-28 rounded-full bg-slate-blue-100/70 blur-2xl" />
            </div>

            <div className="relative z-10 mx-auto w-full max-w-lg text-center lg:mx-0 lg:text-left">
              <div className="mb-8 inline-flex items-center gap-3 rounded-full border border-warm-200 bg-white/90 px-4 py-2.5 shadow-sm backdrop-blur-sm">
                <img
                  src="/assets/logo-transparente-parcial.webp"
                  alt="Tesourinhos"
                  className="h-10 w-10 rounded-full bg-rose-50 p-1.5 object-contain"
                />
                <div className="leading-tight">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-warm-500">
                    Tesourinhos
                  </p>
                  <p className="text-sm font-semibold text-warm-800">Acesso seguro a sua conta</p>
                </div>
              </div>

              <div className="space-y-5">
                <h1 className="text-4xl font-bold leading-tight text-warm-900 sm:text-[2.9rem]">
                  Encontre a babá ideal
                  <br />
                  com segurança
                </h1>

                <p className="max-w-md text-base leading-7 text-warm-600 lg:max-w-lg">
                  Profissionais verificadas, avaliações reais e<br/>
                  suporte da plataforma.
                </p>

              </div>
            </div>
          </div>

          <div className="flex items-center justify-center bg-white px-5 py-8 sm:px-8 lg:px-12 lg:py-12">
            <div className="w-full max-w-md rounded-[28px] border border-warm-100 bg-white p-7 shadow-[0_18px_50px_rgba(46,39,32,0.08)] sm:p-8">
              <h2 className="mb-5 text-center text-xl font-semibold">Entrar</h2>

              <LoginSocial setNotification={setNotification} redirectOnSuccess="/login" />

              <div className="relative my-4 text-center">
                <hr className="border-t border-slate-200" />
                <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-3 text-xs text-slate-500">
                  ou continue com e-mail
                </span>
              </div>

              <form onSubmit={handleLogin} className="flex flex-col" data-cy="login-form">
                <div className="mb-4 flex flex-col">
                  <label className="mb-1.5 text-xs font-semibold text-slate-700">Email</label>
                  <input
                    data-cy="login-email-input"
                    type="email"
                    placeholder="seu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={loading}
                    className="h-11 rounded-lg border border-slate-300 px-3 text-sm transition focus:border-[#4a4e91] focus:outline-none focus:ring-2 focus:ring-[#4a4e91]/20"
                  />
                </div>

                <div className="mb-4 flex flex-col">
                  <label className="mb-1.5 text-xs font-semibold text-slate-700">Senha</label>
                  <div className="relative">
                    <input
                      data-cy="login-password-input"
                      type={showPassword ? "text" : "password"}
                      placeholder="Digite sua senha"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      disabled={loading}
                      className="h-11 w-full rounded-lg border border-slate-300 px-3 pr-10 text-sm transition focus:border-[#4a4e91] focus:outline-none focus:ring-2 focus:ring-[#4a4e91]/20"
                    />
                    <button
                      data-cy="login-toggle-password"
                      type="button"
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 cursor-pointer border-none bg-transparent text-slate-500"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>

                <button
                  data-cy="login-submit-button"
                  className={`mt-1 h-11 w-full rounded-xl bg-rose-400 text-sm font-semibold text-white transition hover:brightness-105 disabled:opacity-70 ${loading ? "opacity-70" : ""}`}
                  disabled={loading}
                >
                  {loading ? "Entrando..." : "Entrar"}
                </button>
              </form>

              <div className="mt-5 flex flex-col items-center gap-2 text-sm">
                <Link to="/recuperar-senha" data-cy="forgot-password-link" className="font-semibold text-[#4a4e91]">
                  Esqueceu sua senha?
                </Link>
                <p className="text-slate-500">
                  Não tem conta?{" "}
                  <Link to="/auth" data-cy="create-account-link" className="font-semibold text-[#4a4e91]">
                    Criar conta
                  </Link>
                </p>
                <Link
                  to="/"
                  data-cy="back-home-link"
                  className="mt-1 inline-flex items-center text-sm font-semibold text-warm-600 underline-offset-4 transition hover:text-warm-800 hover:underline"
                >
                  Voltar para página inicial
                </Link>
              </div>
            </div>
          </div>
        </div>

        <NotificationModal
          show={notification.show}
          message={notification.message}
          type={notification.type}
          onClose={closeNotification}
        />
      </div>
    </>
  );
}